import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import { ArrowLeft, User, Shield, Mail, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/contexts/ThemeContext";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { mobileAnimations } from "@/utils/mobile-animations";
import ProfileSection from "@/components/ProfileSection";
import { Separator } from "@/components/ui/separator";

const ProfilePage: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";
  const isMobile = useIsMobile();
  
  const [activeTab, setActiveTab] = useState<string>("profile");

  // Animation variants
  const pageVariants = mobileAnimations.pageEntry;
  const containerVariants = mobileAnimations.fadeIn;

  return (
    <motion.div 
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className={cn(
        "min-h-screen pb-10",
        isDark ? "bg-gradient-to-b from-gray-900 to-gray-950 text-white" : "bg-gradient-to-b from-blue-50 to-white text-gray-900"
      )}
    >
      {/* Header with back button */}
      <div className={cn(
        "sticky top-0 z-10 backdrop-blur-md p-4 flex items-center justify-between",
        isDark ? "bg-gray-900/80 border-b border-gray-800" : "bg-white/80 border-b border-gray-100"
      )}>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate(-1)} 
          className="flex items-center gap-2"
        >
          <ArrowLeft size={18} /> 
          <span className={isMobile ? "sr-only" : "inline"}>Back</span>
        </Button>
        
        <h1 className="text-xl font-bold">Profile</h1>
        
        <div className="w-10"></div> {/* Empty div for balanced spacing */}
      </div>

      {/* Main content */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-4xl mx-auto px-4 py-6"
      >
        <div className="grid grid-cols-1 gap-6">
          {/* Main profile section */}
          <ProfileSection initialTab={activeTab} />
          
          {/* Account summary (simplified and keeping essentials) */}
          <motion.div
            variants={mobileAnimations.slideInUp}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.3 }}
            className={cn(
              "rounded-xl p-6 space-y-4",
              isDark ? 
                "bg-gradient-to-br from-gray-800/80 to-gray-900/90 border border-gray-700/50" : 
                "bg-white border border-gray-200/80 shadow-sm"
            )}
          >
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Account Information</h2>
            </div>
            
            <Separator className={isDark ? "bg-gray-700/50" : "bg-gray-100"} />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "p-2 rounded-full",
                    isDark ? "bg-gray-700/50 text-blue-400" : "bg-blue-50 text-blue-600"
                  )}>
                    <Mail size={18} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Email Address</p>
                    <p className="font-medium">{currentUser?.email || "Not provided"}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "p-2 rounded-full",
                    isDark ? "bg-gray-700/50 text-blue-400" : "bg-blue-50 text-blue-600"
                  )}>
                    <Calendar size={18} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Joined</p>
                    <p className="font-medium">
                      {currentUser?.metadata?.creationTime 
                        ? new Date(currentUser.metadata.creationTime).toLocaleDateString(undefined, {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          }) 
                        : "Unknown"}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "p-2 rounded-full",
                    isDark ? "bg-gray-700/50 text-blue-400" : "bg-blue-50 text-blue-600"
                  )}>
                    <User size={18} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Display Name</p>
                    <p className="font-medium">{currentUser?.displayName || "Anonymous User"}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "p-2 rounded-full",
                    isDark ? "bg-gray-700/50 text-blue-400" : "bg-blue-50 text-blue-600"
                  )}>
                    <Shield size={18} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Account Status</p>
                    <p className="font-medium">Verified</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="pt-2 flex justify-center md:justify-end">
              <Button
                size="sm"
                className={cn(
                  "font-medium",
                  isDark ? "bg-blue-600 hover:bg-blue-700" : ""
                )}
                onClick={() => navigate('/settings')}
              >
                Edit Profile
              </Button>
            </div>
          </motion.div>
          
          {/* App version */}
          <p className={cn(
            "text-center text-xs pt-4",
            isDark ? "text-gray-500" : "text-gray-400"
          )}>
            App version 1.0.0
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ProfilePage;
